import { useActiveCircle } from '@/hooks/use-active-circle';
import { useGuideQueue } from '@/hooks/use-guide-queue';
import { useProvidedProgramContext } from '@/hooks/use-provided-program-context';
import { useStartJournalingWorkflow } from '@/hooks/use-start-journaling-workflow';
import { startNewJournalEntry } from '@/providers/ai-journaling';
import { JournalSource } from '@/utils/constants';
import { isMindshiftGuide } from '@/utils/guides';
import { MediaGuideMetadata } from '@/utils/media/types';
import { getProgramId, ProgramState } from '@/utils/programs';
import { routes } from '@/utils/routes';
import { EventProperties } from '@calm/evexia-analytics';
import {
    CircleCircle,
    WorkflowStepDataContentPack,
    WorkflowStepInfo,
    WorkflowStepState,
    WorkflowStepType,
    WorkflowWorkflowType,
} from '@calm/evexia-web-sdk';
import { AudioPlayerOpenState } from '@rhg/components/media/audio-player-combined/types';
import { AudioPlayerControls } from '@rhg/components/media/audio-player/types';
import { MediaTrackerFn } from '@rhg/hooks/use-media-tracker/types';
import { useProgramContextWithGuidesAndWorkflow } from '@rhg/hooks/use-program-context/with-guides-and-workflow';
import { invalidatePrograms } from '@rhg/hooks/use-programs';
import { useForceRefreshCircleRecommendations } from '@rhg/hooks/use-recommendations/use-force-refresh-circle-recommendations';
import { useUpdateWorkflowStep } from '@rhg/hooks/use-update-workflow-step';
import { useFetchWorkflowNextStep } from '@rhg/hooks/use-workflow-next-step';
import { logger } from '@rhg/libs/logger';
import {
    loopCapabilityForControls,
    shuffleCapabilityForControls,
    supportsPlaybackSpeedForControls,
} from '@rhg/providers/guide-queue/capabilities';
import { LoopMode } from '@rhg/providers/guide-queue/types';
import { getGuideId, MediaEvent } from '@rhg/utils/media';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
} from 'react';
import { PlaybackMode, TrackToPlay } from '..';
import { PROGRAM_QUEUE_SOURCE, queueSourceKey } from '../queue-source';
import {
    OpenGuidePlayerProps,
    PlayerMode,
    QueueSource,
    QueueSourceKind,
} from '../types';
import { useQueueSourceControls } from './use-queue-source-controls';
import { useWarmAdjacentTracks } from './use-warm-adjacent-tracks';

const GuidePlayer = dynamic(() =>
    import('@rhg/components/guide/player').then((mod) => mod.GuidePlayer)
);

export interface ActiveSessionProps {
    isOpen: boolean;
    playerMode: PlayerMode;
    setOpenState: (state: AudioPlayerOpenState) => void;
    playingGuide: MediaGuideMetadata;
    playingGuideId: string;
    trackFn: MediaTrackerFn | undefined;
    willSkipToPreviousProgress: boolean;
    sessionTrackProps: EventProperties;
    /** Source of the loaded queue. Omitted means the playing guide's program. */
    queueSource?: QueueSource;
    /** Source-level props carried by every session event (e.g. PlaylistId). */
    queueTrackProps?: EventProperties;
    setIsGuideCompletedOpen: Dispatch<SetStateAction<boolean>>;
    setCompletedContentStep: Dispatch<
        SetStateAction<WorkflowStepInfo | undefined>
    >;
    closeGuidePlayer: (isTriggeredFromTrackChange?: boolean) => void;
    openGuidePlayer: (props: OpenGuidePlayerProps) => Promise<void>;
    onMinimize: () => void;
    minimizeForNavigation: () => void;
    activeCircle: CircleCircle | undefined;
    activeCircleId: number | undefined;
    setIsStartingJournaling: (value: boolean) => void;
    /** Open the queue already shuffled (header Shuffle CTA starts shuffled playback). */
    willShuffle: boolean;
}

export function GuidePlayerActiveSession({
    isOpen,
    playerMode,
    setOpenState,
    playingGuide,
    playingGuideId,
    trackFn,
    willSkipToPreviousProgress,
    sessionTrackProps,
    queueSource = PROGRAM_QUEUE_SOURCE,
    queueTrackProps,
    setIsGuideCompletedOpen,
    setCompletedContentStep,
    closeGuidePlayer,
    openGuidePlayer,
    onMinimize,
    minimizeForNavigation,
    activeCircle,
    activeCircleId,
    setIsStartingJournaling,
    willShuffle,
}: ActiveSessionProps) {
    // Derive the combined AudioPlayerOpenState for GuidePlayer and track change logic
    const openState = isOpen ? playerMode : false;
    // program is already fetched by ActiveProgramProvider above us in the tree;
    // read from context rather than issuing a duplicate useProgram call.
    const { program, packClass } = useProvidedProgramContext();
    const programContext = useProgramContextWithGuidesAndWorkflow({
        program,
        packClass,
    });
    const { push } = useRouter();
    const { startJournalingWorkflow } = useStartJournalingWorkflow();

    const { hasPermission } = useActiveCircle();
    // Create and read: this path calls startNewJournalEntry/startJournalingWorkflow
    // and then opens the entry, so a circle missing either must fall through to
    // the normal session-end flow.
    const canCreateAndReadJournalEntry = hasPermission('ai_journaling', [
        'c',
        'r',
    ]);
    const isJournalingGuide = !!playingGuide.metadata?.journal_id;

    const { updateWorkflowStepAsync } = useUpdateWorkflowStep();
    const { fetchWorkflowNextStepAsync } = useFetchWorkflowNextStep();
    const { forceRefreshRecommendations } =
        useForceRefreshCircleRecommendations();

    const sessionsAndCurrentSessionIndex = useMemo(() => {
        const sessions =
            queueSource.kind === QueueSourceKind.Playlist
                ? queueSource.guides
                : programContext.orderedSessions.filter(
                      ({ isTrailer }) => !isTrailer
                  );
        const currentSessionIndex = sessions.findIndex(
            (guide) => getGuideId(guide) === playingGuideId
        );
        return { sessions, currentSessionIndex };
    }, [playingGuideId, programContext.orderedSessions, queueSource]);

    const { setQueue, next, previous, advanceNext, setCurrentIndex, loopMode } =
        useGuideQueue();

    useWarmAdjacentTracks({
        ...sessionsAndCurrentSessionIndex,
        next,
        previous,
        queueSource,
        packClass: programContext.packClass,
    });

    const audioControls = useQueueSourceControls({
        queueSource,
        program,
        playingGuide,
    });

    // Derived from audioControls rather than program.meditationType/totalGuides
    // directly — audioControls already excludes clinical/mindshift/trailer/
    // dailies content (forcing Regular), so a clinical or mindshift program
    // with multiple sessions is correctly kept out of Playlist here too. That
    // matters because Playlist drives raw track-end auto-advance below
    // (willAdvanceOnTrackEnd) — clinical/mindshift programs must instead fall
    // through to the post-session dialog flow in onProgress. Having two
    // independent classifiers previously drifted out of sync (EVE-13636).
    const playbackMode = useMemo(() => {
        if (audioControls === AudioPlayerControls.Soundscape)
            return PlaybackMode.Soundscape;
        if (
            audioControls === AudioPlayerControls.Playlist ||
            audioControls === AudioPlayerControls.MusicAlbum
        )
            return PlaybackMode.Playlist;
        return PlaybackMode.Normal;
    }, [audioControls]);

    const supportsPlaybackSpeed =
        supportsPlaybackSpeedForControls(audioControls);

    const sourceKey =
        queueSource.kind === QueueSourceKind.Playlist
            ? queueSourceKey(queueSource, playingGuide)
            : undefined;
    // Android parity: a multi-track playlist runs no journaling or completion flow after a track.
    const isMultiTrackPlaylist =
        queueSource.kind === QueueSourceKind.Playlist &&
        queueSource.guides.length > 1;

    useEffect(() => {
        setQueue({
            guides: sessionsAndCurrentSessionIndex.sessions,
            capability: loopCapabilityForControls(audioControls),
            isShuffleSupported: shuffleCapabilityForControls(audioControls),
            // Header Shuffle CTA launched playback → open shuffled. setQueue's
            // isSameContext guard ignores this on track-change re-opens, so
            // shuffle is not re-randomised on skip/play/pause.
            willShuffle,
            sourceKey,
        });
    }, [
        sessionsAndCurrentSessionIndex.sessions,
        audioControls,
        setQueue,
        willShuffle,
        sourceKey,
    ]);

    // Keep the queue's notion of the playing index in sync so toggleShuffle can
    // hold the current track in place when shuffle is enabled (EVE-11931, Android parity).
    const { currentSessionIndex } = sessionsAndCurrentSessionIndex;
    useEffect(() => {
        setCurrentIndex(currentSessionIndex);
    }, [currentSessionIndex, setCurrentIndex]);

    // True when the player should keep playing after the current track ends —
    // i.e. the queue will advance to another track. This covers both the
    // mid-playlist case and a Loop-All wrap from the last track back to the
    // first. Delegating to the queue's `next()` keeps this in lockstep with the
    // loop mode (Loop Off on the last track returns null → the player closes).
    const willAdvanceOnTrackEnd = useMemo(() => {
        if (playbackMode !== PlaybackMode.Playlist) return false;
        const { currentSessionIndex } = sessionsAndCurrentSessionIndex;
        return currentSessionIndex >= 0 && next(currentSessionIndex) !== null;
    }, [playbackMode, sessionsAndCurrentSessionIndex, next]);

    const onTrackChange = useCallback(
        (trackToPlay: TrackToPlay) => {
            const { sessions, currentSessionIndex } =
                sessionsAndCurrentSessionIndex;

            if (
                currentSessionIndex < 0 ||
                sessions.length === 0 ||
                !programContext.program
            ) {
                return;
            }

            const newTrackIndex =
                trackToPlay === TrackToPlay.Next
                    ? advanceNext(currentSessionIndex)
                    : previous(currentSessionIndex);

            if (newTrackIndex === null) {
                return;
            }

            const isClinicalProgram =
                programContext?.programType ===
                WorkflowWorkflowType.ClinicalProgram;
            const isInProgressClinicalProgram =
                programContext.programState === ProgramState.InProgress &&
                isClinicalProgram;
            if (isClinicalProgram && !isInProgressClinicalProgram) {
                closeGuidePlayer(false);
                return;
            }

            closeGuidePlayer(true);
            const newGuide = sessions[newTrackIndex];
            if (queueSource.kind === QueueSourceKind.Playlist) {
                void openGuidePlayer({
                    openState,
                    guide: newGuide,
                    packClass:
                        queueSource.packClassByProgramId[newGuide.programId],
                    trackFn,
                    willSkipToPreviousProgress: false,
                    queueSource,
                });
                return;
            }
            void openGuidePlayer({
                openState,
                guide: newGuide,
                packClass: programContext.packClass,
                tools: programContext.toolsByGuide[getGuideId(newGuide)],
                trackFn,
                willSkipToPreviousProgress: false,
            });
        },
        [
            sessionsAndCurrentSessionIndex,
            advanceNext,
            previous,
            closeGuidePlayer,
            openGuidePlayer,
            openState,
            programContext.packClass,
            programContext.program,
            programContext.programType,
            programContext.programState,
            programContext.toolsByGuide,
            trackFn,
            queueSource,
        ]
    );

    const advanceProgramWorkflowStep = useCallback(async () => {
        const isEverySessionIsATrailerCompletedOrCurrentGuide =
            programContext?.orderedSessions.every(
                (session) =>
                    session.isTrailer ||
                    session.completed ||
                    getGuideId(session) === playingGuideId
            );

        const hasContentPackStep =
            programContext?.currentWorkflowStep?.stepDefn.type ===
            WorkflowStepType.ContentPack;
        const shouldUpdateWorkflow =
            isEverySessionIsATrailerCompletedOrCurrentGuide &&
            hasContentPackStep;

        if (!shouldUpdateWorkflow) {
            return;
        }

        const stepData = programContext?.currentWorkflowStep?.stepDefn
            ?.data as WorkflowStepDataContentPack;

        if (stepData.clientStateUpdate) {
            await updateWorkflowStepAsync({
                workflowDefnId: programContext?.workflow?.workflowDefn?.defnId,
                stepDefnId:
                    programContext?.currentWorkflowStep?.stepDefn?.defnId,
                workflowType: programContext?.workflow?.workflowDefn.type,
                request: { state: WorkflowStepState.Completed },
            });
        } else {
            await fetchWorkflowNextStepAsync({
                workflowDefnId: programContext?.workflow?.workflowDefn.defnId,
                workflowType: programContext?.workflow?.workflowDefn.type,
                circle: activeCircle!,
            });

            const programContextId = programContext?.program
                ? getProgramId(programContext.program)
                : undefined;
            if (
                programContextId &&
                programContext?.packClass &&
                activeCircleId
            ) {
                await invalidatePrograms({
                    programIds: new Set([programContextId]),
                    circleId: activeCircleId,
                });
            }
        }

        forceRefreshRecommendations({ circle: activeCircle });
        setCompletedContentStep(programContext?.currentWorkflowStep);
    }, [
        programContext?.orderedSessions,
        programContext?.currentWorkflowStep,
        programContext?.workflow?.workflowDefn,
        programContext?.program,
        programContext?.packClass,
        playingGuideId,
        forceRefreshRecommendations,
        activeCircle,
        updateWorkflowStepAsync,
        fetchWorkflowNextStepAsync,
        activeCircleId,
        setCompletedContentStep,
    ]);

    const handlePostSessionWorkflowAndDialogs = useCallback(async () => {
        await advanceProgramWorkflowStep();
        setIsGuideCompletedOpen(true);
    }, [advanceProgramWorkflowStep, setIsGuideCompletedOpen]);

    const handleJournalingGuideCompletion = useCallback(async () => {
        setIsStartingJournaling(true);
        try {
            await advanceProgramWorkflowStep();
            startNewJournalEntry();
            await startJournalingWorkflow();
        } catch (err) {
            logger.error(
                'Failed to start journaling flow after guide completion',
                {},
                err as Error
            );
            setIsStartingJournaling(false);
            setIsGuideCompletedOpen(true);
            return;
        }
        closeGuidePlayer();
        const programId = programContext?.program
            ? getProgramId(programContext.program)
            : undefined;
        await push(
            routes.journalEntry({
                programId,
                guideId: playingGuideId,
                source: JournalSource.Content,
            })
        );
    }, [
        advanceProgramWorkflowStep,
        startJournalingWorkflow,
        closeGuidePlayer,
        push,
        playingGuideId,
        setIsGuideCompletedOpen,
        setIsStartingJournaling,
        programContext?.program,
    ]);

    const onProgress = useCallback<MediaTrackerFn>(
        (event) => {
            trackFn?.(event);

            if (
                event.eventType !== MediaEvent.Ended ||
                willAdvanceOnTrackEnd ||
                // Any active loop keeps the player open: Loop One repeats the
                // current track (the audio element loops in place) and Loop All
                // wraps the queue. Only a non-looping end should close.
                loopMode !== LoopMode.Off ||
                playbackMode === PlaybackMode.Soundscape
            ) {
                return;
            }

            // Only interrupt with journaling when the player is expanded and
            // the page is visible; a minimized player or a hidden tab/window
            // means the user is doing something else, so fall through to the
            // normal session-end flow as if the guide had no journaling
            // exercise.
            if (
                canCreateAndReadJournalEntry &&
                !isMultiTrackPlaylist &&
                isJournalingGuide &&
                playerMode === PlayerMode.Dialog &&
                document.visibilityState === 'visible'
            ) {
                setOpenState(false);
                void handleJournalingGuideCompletion();
                return;
            }

            setIsGuideCompletedOpen(false);
            setCompletedContentStep(undefined);
            setOpenState(false);

            const isInProgressClinicalProgram =
                programContext.programState === ProgramState.InProgress &&
                programContext?.programType ===
                    WorkflowWorkflowType.ClinicalProgram;
            const isMindshift = isMindshiftGuide(playingGuide);
            const hasPostSessionWorkflow =
                isMindshift || isInProgressClinicalProgram;
            const isTrailerOrNotApplicableProgramType =
                playingGuide?.isTrailer || !hasPostSessionWorkflow;

            if (isMultiTrackPlaylist || isTrailerOrNotApplicableProgramType) {
                closeGuidePlayer();
                return;
            }

            void handlePostSessionWorkflowAndDialogs();
        },
        [
            playbackMode,
            trackFn,
            willAdvanceOnTrackEnd,
            loopMode,
            isMultiTrackPlaylist,
            playingGuide,
            programContext?.programType,
            programContext.programState,
            handlePostSessionWorkflowAndDialogs,
            closeGuidePlayer,
            setOpenState,
            setIsGuideCompletedOpen,
            setCompletedContentStep,
            canCreateAndReadJournalEntry,
            isJournalingGuide,
            playerMode,
            handleJournalingGuideCompletion,
        ]
    );

    return (
        <GuidePlayer
            openState={openState}
            setOpenState={setOpenState}
            guide={playingGuide}
            context={programContext}
            willSkipToPreviousProgress={willSkipToPreviousProgress}
            onClose={closeGuidePlayer}
            onMinimize={onMinimize}
            onNavigationLinkMinimize={minimizeForNavigation}
            onProgress={onProgress}
            onTrackChange={onTrackChange}
            playbackMode={playbackMode}
            audioControls={audioControls}
            loopMode={loopMode}
            supportsPlaybackSpeed={supportsPlaybackSpeed}
            tools={programContext?.toolsByGuide[playingGuideId]}
            sessionTrackProps={sessionTrackProps}
            queueTrackProps={queueTrackProps}
        />
    );
}
